import { ApolloClient, InMemoryCache, HttpLink, split } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { WebSocketLink } from "@apollo/client/link/ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { SubscriptionClient } from "subscriptions-transport-ws";

const apiUrl = process.env.REACT_APP_HASURA_GRAPHQL_ENDPOINT;
const wsApiUrl = process.env.REACT_APP_HASURA_WS_GRAPHQL_ENDPOINT;
const apiKey = process.env.REACT_APP_HASURA_ADMIN_SECRET;

if (!apiUrl || !apiKey || !wsApiUrl) {
  throw new Error(
    "API URL or API Key is not defined in environment variables.",
  );
}

/**
 * Helper function to get the company UUID from localStorage
 * This is used for Hasura row-level security filtering via x-hasura-company-id header
 */
const getCompanyId = (): string | null => {
  return localStorage.getItem("selectedCompanyUuid");
};

const httpLink = new HttpLink({
  uri: apiUrl,
});

/**
 * Context link that dynamically adds headers to each GraphQL request
 * This ensures the company_id is always current (handles company switching)
 */
const authLink = setContext((_, { headers }) => {
  const companyId = getCompanyId();

  return {
    headers: {
      ...headers,
      "x-hasura-admin-secret": apiKey,
      ...(companyId && { "x-hasura-company-id": companyId }),
    },
  };
});

console.log("[Apollo] Initializing WebSocket connection to:", wsApiUrl);

const subscriptionClient = new SubscriptionClient(wsApiUrl, {
  reconnect: true,
  connectionParams: () => {
    // Use a function to get fresh company_id on each connection/reconnection
    const companyId = getCompanyId();
    return {
      headers: {
        "x-hasura-admin-secret": apiKey,
        ...(companyId && { "x-hasura-company-id": companyId }),
      },
    };
  },
  connectionCallback: (error: Error[]) => {
    if (error && error.length > 0) {
      console.error("[Apollo] WebSocket connection callback error:", error);
    } else {
      console.log(
        "[Apollo] WebSocket connection callback: connected successfully",
      );
    }
  },
  reconnectionAttempts: 5,
  timeout: 30000,
  lazy: false, // Force immediate connection
});

// Add event listeners after creating the client
subscriptionClient.onConnected(() => {
  console.log("[Apollo] WebSocket onConnected event fired");
});

subscriptionClient.onReconnected(() => {
  console.log("[Apollo] WebSocket onReconnected event fired");
});

subscriptionClient.onDisconnected(() => {
  console.log("[Apollo] WebSocket onDisconnected event fired");
});

subscriptionClient.onError((error: Error) => {
  console.error("[Apollo] WebSocket onError event:", error);
});

subscriptionClient.onReconnecting(() => {
  console.log("[Apollo] WebSocket onReconnecting event fired");
});

// Try to get connection status
console.log("[Apollo] WebSocket client status:", subscriptionClient.status);

const wsLink = new WebSocketLink(subscriptionClient);

// Chain authLink with httpLink for HTTP requests (queries and mutations)
const httpLinkWithAuth = authLink.concat(httpLink);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLinkWithAuth,
);

const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
});

/**
 * Reconnect the WebSocket with updated connection params.
 * Call this function when the company_id changes (e.g., after company switch)
 * to ensure subscriptions use the new company filter.
 */
export const reconnectWebSocket = (): void => {
  console.log("[Apollo] Reconnecting WebSocket with updated company_id...");
  subscriptionClient.close(false, false);
  // The reconnect: true option will automatically reconnect with fresh connectionParams
};

export default client;
